import { ShieldAlert, ShieldCheck, Lock, LockOpen, Server, Globe, FileDown } from "lucide-react";
import { exportScanPdf } from "@/lib/pdf";

const gradeColor = (g) =>
  ({ A: "#22e07a", B: "#7ee787", C: "#ffd166", D: "#ff9f43", F: "#ff2d6f" }[g] || "#00f0ff");

export const ScanResult = ({ scan }) => {
  if (!scan) return null;
  const gc = gradeColor(scan.grade);
  return (
    <div className="space-y-6" data-testid="scan-result">
      {/* Score header */}
      <div className="flex flex-wrap items-center justify-between gap-6 rounded-2xl border border-white/10 bg-white/[0.03] p-6">
        <div>
          <p className="font-mono-tech text-[11px] uppercase tracking-[0.2em] text-white/50">Objetivo</p>
          <p className="mt-1 flex items-center gap-2 font-head text-xl font-bold text-white">
            <Globe className="h-5 w-5 text-cyber-cyan" /> {scan.target}
          </p>
          <p className="mt-1 font-mono-tech text-xs text-white/40">{scan.ip}</p>
          <button
            onClick={() => { exportScanPdf(scan); }}
            data-testid="export-pdf"
            className="mt-3 inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 font-mono-tech text-[11px] uppercase tracking-widest text-white/80 transition-colors duration-300 hover:border-cyber-cyan hover:text-cyber-cyan"
          >
            <FileDown className="h-3.5 w-3.5" /> Exportar PDF
          </button>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-right">
            <p className="font-mono-tech text-[11px] uppercase tracking-[0.2em] text-white/50">Puntuación</p>
            <p className="font-head text-3xl font-black text-white">{scan.score}<span className="text-base text-white/40">/100</span></p>
          </div>
          <div
            className="flex h-16 w-16 items-center justify-center rounded-full font-head text-3xl font-black"
            style={{ color: gc, border: `2px solid ${gc}`, boxShadow: `0 0 24px ${gc}55` }}
          >
            {scan.grade}
          </div>
        </div>
      </div>

      <div className="grid gap-5 lg:grid-cols-3">
        {/* Ports */}
        <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-6">
          <h4 className="flex items-center gap-2 font-head text-sm font-semibold text-white">
            <Server className="h-4 w-4 text-cyber-cyan" /> Puertos abiertos ({scan.ports.length})
          </h4>
          <div className="mt-4 space-y-2">
            {scan.ports.length === 0 && <p className="font-sans text-sm text-white/40">Ningún puerto común abierto.</p>}
            {scan.ports.map((p) => (
              <div key={p.port} className="flex items-center justify-between rounded-lg bg-white/[0.03] px-3 py-2">
                <span className="font-mono-tech text-xs text-white">{p.port} · {p.service}</span>
                <span
                  className="rounded-full px-2 py-0.5 font-mono-tech text-[10px] uppercase"
                  style={{
                    color: p.risk === "high" ? "#ff2d6f" : "#22e07a",
                    background: p.risk === "high" ? "#ff2d6f22" : "#22e07a22",
                  }}
                >
                  {p.risk === "high" ? "riesgo" : "ok"}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Headers */}
        <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-6">
          <h4 className="flex items-center gap-2 font-head text-sm font-semibold text-white">
            <ShieldCheck className="h-4 w-4 text-cyber-cyan" /> Cabeceras de seguridad
          </h4>
          <div className="mt-4 space-y-2">
            {scan.headers.present.map((h) => (
              <p key={h} className="flex items-center gap-2 font-sans text-xs text-white/70">
                <ShieldCheck className="h-3.5 w-3.5 text-[#22e07a]" /> {h}
              </p>
            ))}
            {scan.headers.missing.map((h) => (
              <p key={h} className="flex items-center gap-2 font-sans text-xs text-white/50">
                <ShieldAlert className="h-3.5 w-3.5 text-[#ff9f43]" /> Falta: {h}
              </p>
            ))}
          </div>
        </div>

        {/* TLS */}
        <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-6">
          <h4 className="flex items-center gap-2 font-head text-sm font-semibold text-white">
            {scan.tls.valid ? <Lock className="h-4 w-4 text-[#22e07a]" /> : <LockOpen className="h-4 w-4 text-[#ff2d6f]" />}
            Certificado TLS
          </h4>
          <div className="mt-4 space-y-2 font-sans text-xs text-white/60">
            {scan.tls.valid ? (
              <>
                <p>Protocolo: <span className="text-white">{scan.tls.protocol}</span></p>
                <p>Emisor: <span className="text-white">{scan.tls.issuer || "—"}</span></p>
                <p>CN: <span className="text-white">{scan.tls.common_name || "—"}</span></p>
                <p>Expira: <span className="text-white">{scan.tls.expires}</span></p>
                <p>Días restantes: <span className={scan.tls.days_left < 15 ? "text-[#ff9f43]" : "text-[#22e07a]"}>{scan.tls.days_left}</span></p>
              </>
            ) : (
              <p className="text-[#ff9f43]">Sin HTTPS válido en el puerto 443.</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
